import type { ClientDraftContentPlatform, ClientDraftItem, ClientDraftVersion } from '@/api/beatlyApi';
import type { Collaborator, Post, PostCompliance } from '@/components/ContentReviewSection';
import { isDisplayableCollaborationThumbUrl } from '@/utils/mapCampaignCollaborationsToCollaborators';

/**
 * Content Review uses GET /campaign/:id/collaborations; Content Approval uses GET /campaign/:id/drafts.
 * The drafts payload usually includes `medias` + poster URLs even when collaborations omit previews.
 * We merge draft-derived visuals into review rows so thumbnails match what users see in approval.
 */

const EMPTY_COMPLIANCE: PostCompliance = {
  brandTagged: false,
  hashtagUsed: false,
  productVisible: false,
  adDisclosure: false,
  linkIncluded: false,
};

/**
 * Match collaborations ↔ drafts even when one side sends a profile URL and the other a bare handle.
 */
function normalizeHandle(h: string | undefined): string {
  const base = (h ?? '').trim().replace(/^@+/u, '').toLowerCase();
  if (!base) return '';
  if (!/^https?:\/\//i.test(base)) return base;
  try {
    const u = new URL(base);
    const host = u.hostname.toLowerCase();
    const parts = u.pathname.split('/').filter(Boolean);
    const first = parts[0]?.replace(/^@/, '') ?? '';
    const skipIg = new Set(['p', 'reel', 'reels', 'stories', 'explore', 'tv', 'accounts']);
    if (host.endsWith('instagram.com') && first && !skipIg.has(first)) {
      return first;
    }
    if (host.endsWith('tiktok.com')) {
      const atSeg = parts.find((p) => p.startsWith('@'));
      if (atSeg) return atSeg.replace(/^@/, '');
      if (first && !/^\d+$/.test(first)) return first.replace(/^@/, '');
    }
    if (host === 'youtu.be' && parts[0]) return parts[0];
  } catch {
    /* ignore */
  }
  return base;
}

function formatCollabDate(iso: string | undefined | null): string {
  if (!iso || typeof iso !== 'string') return '–';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '–';
  }
}

function isDraftListVideoUrl(url: string): boolean {
  if (!url) return false;
  const [base] = url.split('?');
  return /\.(mp4|mov|avi|webm|ogg|m4v)$/i.test(base);
}

function pickVersionForReview(item: ClientDraftItem): ClientDraftVersion | undefined {
  const vs = item.versions;
  if (!vs?.length) return undefined;
  const lastApproved = [...vs].reverse().find((v) => v.status === 'approved');
  return lastApproved ?? vs[vs.length - 1];
}

/**
 * Same strategy as `pickDraftListPreview` in SeeCampaignsPage: prefer a final image for list thumbs;
 * else final video (with poster frame if any); else fall back to raw pool.
 */
function extractDraftPreview(v: ClientDraftVersion): { thumbnail: string; isVideo: boolean; caption: string } {
  const medias = v.medias ?? [];
  const finalMedias = medias.filter((m) => !m.is_raw);
  const pool = finalMedias.length > 0 ? finalMedias : medias;
  const caption = typeof v.caption === 'string' && v.caption.trim() ? v.caption.trim() : '';

  let thumbnail = '';
  let isVideo = false;

  if (pool.length === 0) {
    if (typeof v.thumbnail_url === 'string' && v.thumbnail_url.length > 0) {
      thumbnail = v.thumbnail_url;
      isVideo = isDraftListVideoUrl(thumbnail);
    }
    return { thumbnail, isVideo, caption };
  }

  const firstImage = pool.find((m) => m.resource_type === 'image' && m.url);
  if (firstImage?.url) {
    return { thumbnail: firstImage.url, isVideo: false, caption };
  }
  const firstVideo = pool.find((m) => m.resource_type === 'video' && m.url);
  const videoMedia = firstVideo ?? pool.find((m) => m.url && (m.resource_type === 'video' || isDraftListVideoUrl(m.url)));
  if (videoMedia?.url) {
    const poster =
      (typeof v.thumbnail_url === 'string' &&
        v.thumbnail_url.length > 0 &&
        !isDraftListVideoUrl(v.thumbnail_url)
        ? v.thumbnail_url
        : undefined) ?? pool.find((m) => m.resource_type === 'image')?.url;
    return {
      thumbnail: poster ?? videoMedia.url,
      isVideo: true,
      caption,
    };
  }

  const first = pool[0];
  if (first?.url) {
    if (first.resource_type === 'video' || isDraftListVideoUrl(first.url)) {
      const poster =
        (typeof v.thumbnail_url === 'string' &&
          v.thumbnail_url.length > 0 &&
          !isDraftListVideoUrl(v.thumbnail_url)
          ? v.thumbnail_url
          : undefined) ?? pool.find((m) => m.resource_type === 'image')?.url;
      return { thumbnail: poster ?? first.url, isVideo: true, caption };
    }
    return { thumbnail: first.url, isVideo: false, caption };
  }

  if (typeof v.thumbnail_url === 'string' && v.thumbnail_url.length > 0) {
    thumbnail = v.thumbnail_url;
    isVideo = isDraftListVideoUrl(thumbnail);
  }
  return { thumbnail, isVideo, caption };
}

function inferPostTypeFromDraftLabel(label: string | undefined): Post['type'] {
  const blob = (label ?? '').toUpperCase();
  if (blob.includes('STORY')) return 'Story';
  if (blob.includes('CAROUSEL') || blob.includes('CARROUSEL')) return 'Carousel';
  return 'Reel';
}

function draftItemToPost(item: ClientDraftItem, platform: Collaborator['platform']): Post {
  const v = pickVersionForReview(item);
  const { thumbnail, isVideo, caption } = v ? extractDraftPreview(v) : { thumbnail: '', isVideo: false, caption: '' };
  const type = inferPostTypeFromDraftLabel(item.content_type);
  const dateIso = item.publication_date ?? v?.draft_date;
  const permalink =
    typeof v?.permalink === 'string' && v.permalink.trim() ? v.permalink.trim() : undefined;

  const out: Post = {
    type,
    thumbnail,
    caption: caption || (typeof item.content_type === 'string' && item.content_type.trim() ? item.content_type : '–'),
    metrics_from_api: false,
    compliance_from_api: false,
    likes: 0,
    comments: 0,
    date: formatCollabDate(dateIso ?? null),
    isVideo: Boolean(item.is_video) || isVideo,
    reach: 0,
    impressions: 0,
    saves: 0,
    shares: 0,
    engagementRate: 0,
    linkClicks: 0,
    hashtags: [],
    brandTags: [],
    compliance: { ...EMPTY_COMPLIANCE },
    platform,
    ...(permalink ? { permalink } : {}),
  };
  if (typeof item.product_id === 'string' && item.product_id.trim()) {
    out.product_id = item.product_id.trim();
  }
  if (typeof item.product_type === 'string' && item.product_type.trim()) {
    out.product_type = item.product_type.trim();
  }
  return out;
}

function draftsForCollaboratorHandle(c: Collaborator, drafts: ClientDraftItem[]): ClientDraftItem[] {
  const h = normalizeHandle(c.handle);
  const byName = normalizeHandle(c.name);
  return drafts.filter((d) => {
    const dh = normalizeHandle(d.handle);
    if (dh && h && dh === h) return true;
    const dn = normalizeHandle(d.creator_name);
    if (dn && byName && dn === byName) return true;
    return false;
  });
}

/** `c35a4e40-0001-548890c9-col` → `c35a4e40-0001-548890c9` (matches start of `draft_id`). */
function collaborationPrefixFromCollaborationId(collaborationId: string): string | undefined {
  const t = collaborationId.trim();
  if (!t) return undefined;
  if (t.toLowerCase().endsWith('-col')) return t.slice(0, -'-col'.length);
  return undefined;
}

/** `c35a4e40-0001-548890c9-001-01-ver` → `c35a4e40-0001-548890c9` (strip `-<product>-<version>-ver`). */
function collaborationPrefixFromDraftId(draftId: string): string | undefined {
  const parts = draftId.trim().split('-');
  if (parts.length < 4) return undefined;
  if (parts[parts.length - 1].toLowerCase() !== 'ver') return undefined;
  return parts.slice(0, -3).join('-');
}

/**
 * Prefer API `collaboration_id` when present; else scope by `draft_id` prefix so duplicate handles
 * (e.g. two influencers both `tanyainsta`) do not share the same draft list.
 */
function draftsForCollaborator(c: Collaborator, drafts: ClientDraftItem[]): ClientDraftItem[] {
  const cid = c.collaborationId?.trim();
  if (cid) {
    const byCollab = drafts.filter((d) => (d.collaboration_id ?? '').trim() === cid);
    if (byCollab.length > 0) return byCollab;
  }

  const byHandle = draftsForCollaboratorHandle(c, drafts);
  const prefix = cid ? collaborationPrefixFromCollaborationId(cid) : undefined;
  if (prefix) {
    const scoped = byHandle.filter((d) => {
      const did = (d.draft_id ?? '').trim();
      if (!did) return false;
      return did.startsWith(`${prefix}-`) || collaborationPrefixFromDraftId(did) === prefix;
    });
    if (scoped.length > 0) return scoped;
  }
  return byHandle;
}

function platformFromDraft(p?: ClientDraftContentPlatform): Collaborator['platform'] {
  if (p === 'tiktok') return 'TikTok';
  if (p === 'youtube') return 'YouTube';
  return 'Instagram';
}

/** Stable numeric id for React keys (same pattern as collaboration mapper). */
function stableRowId(seed: string, salt: number): number {
  let h = salt;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const n = Math.abs(h);
  return n || 1;
}

/** When `/collaborations` returns no rows but `/drafts` has data, still show Content Review. */
function collaboratorsFromDraftsOnly(drafts: ClientDraftItem[]): Collaborator[] {
  const byKey = new Map<string, ClientDraftItem[]>();
  for (const d of drafts) {
    const did = (d.draft_id ?? '').trim();
    const prefix = did ? collaborationPrefixFromDraftId(did) : undefined;
    const key =
      prefix ||
      normalizeHandle(d.handle) ||
      normalizeHandle(d.creator_name) ||
      `draft-${byKey.size}`;
    const list = byKey.get(key) ?? [];
    list.push(d);
    byKey.set(key, list);
  }

  const rows: Collaborator[] = [];
  let salt = 0;
  for (const [key, items] of byKey) {
    const first = items[0];
    const platform = platformFromDraft(first.content_platform);
    const posts = items.map((item) => draftItemToPost(item, platform));
    const latestPub = items
      .map((i) => i.publication_date)
      .filter((x): x is string => Boolean(x && String(x).trim()))
      .sort()
      .slice(-1)[0];

    const draftInfluencerId =
      typeof first.influencer_id === 'string' && first.influencer_id.trim() ? first.influencer_id.trim() : undefined;
    rows.push({
      id: stableRowId(key, salt++),
      ...(draftInfluencerId ? { influencerId: draftInfluencerId } : {}),
      name:
        typeof first.creator_name === 'string' && first.creator_name.trim()
          ? first.creator_name.trim()
          : first.handle ?? '–',
      handle: first.handle ?? '–',
      avatar: typeof first.avatar_url === 'string' ? first.avatar_url : '',
      beat: '–',
      followers: '–',
      engRate: '–',
      deadline: '–',
      publication: latestPub ? formatCollabDate(latestPub) : '–',
      status: 'Published',
      platform,
      address: '–',
      posts,
      product_delivery: null,
      address_available: false,
      compliance_available: false,
      trackers: [],
    });
  }
  return rows.sort((a, b) => (a.collaborationId ?? a.handle).localeCompare(b.collaborationId ?? b.handle));
}

function hasDirectMediaThumbnail(p: Post): boolean {
  const t = p.thumbnail?.trim() ?? '';
  return isDisplayableCollaborationThumbUrl(t);
}

function postProductKey(p: Post): string | undefined {
  const id = p.product_id?.trim();
  return id || undefined;
}

function mergeCollaboratorPosts(apiPosts: Post[], draftPosts: Post[]): Post[] {
  if (draftPosts.length === 0) return apiPosts;
  if (apiPosts.length === 0) return draftPosts;

  const draftByProduct = new Map<string, Post>();
  for (const d of draftPosts) {
    const k = postProductKey(d);
    if (k && hasDirectMediaThumbnail(d) && !draftByProduct.has(k)) draftByProduct.set(k, d);
  }

  const merged = apiPosts.map((p, i) => {
    if (hasDirectMediaThumbnail(p)) return p;
    const pk = postProductKey(p);
    if (pk) {
      const byId = draftByProduct.get(pk);
      if (byId && hasDirectMediaThumbnail(byId)) {
        return {
          ...p,
          thumbnail: byId.thumbnail,
          isVideo: byId.isVideo || p.isVideo,
          caption: p.caption?.trim() && p.caption !== '–' ? p.caption : byId.caption,
        };
      }
    }
    const d = draftPosts[i];
    if (d && hasDirectMediaThumbnail(d)) {
      return {
        ...p,
        thumbnail: d.thumbnail,
        isVideo: d.isVideo || p.isVideo,
        caption: p.caption?.trim() && p.caption !== '–' ? p.caption : d.caption,
      };
    }
    return p;
  });

  const draftPool = draftPosts.filter((d) => hasDirectMediaThumbnail(d));
  let qi = 0;
  return merged.map((p) => {
    if (hasDirectMediaThumbnail(p)) return p;
    const pk = postProductKey(p);
    if (pk) {
      const byId = draftByProduct.get(pk);
      if (byId && hasDirectMediaThumbnail(byId)) {
        return {
          ...p,
          thumbnail: byId.thumbnail,
          isVideo: byId.isVideo || p.isVideo,
          caption: p.caption?.trim() && p.caption !== '–' ? p.caption : byId.caption,
        };
      }
    }
    const d = draftPool[qi++];
    if (!d) return p;
    return {
      ...p,
      thumbnail: d.thumbnail,
      isVideo: d.isVideo || p.isVideo,
      caption: p.caption?.trim() && p.caption !== '–' ? p.caption : d.caption,
    };
  });
}

/** Enrich collaborations-based rows with draft medias when the collaborations API omits previews. */
export function mergeDraftsIntoCollaboratorsForReview(
  collaborators: Collaborator[],
  drafts: ClientDraftItem[]
): Collaborator[] {
  if (!drafts?.length) return collaborators;

  if (collaborators.length === 0) {
    return collaboratorsFromDraftsOnly(drafts);
  }

  return collaborators.map((c) => {
    const items = draftsForCollaborator(c, drafts);
    if (items.length === 0) return c;

    const draftPosts = items.map((item) => draftItemToPost(item, c.platform));
    const mergedPosts = mergeCollaboratorPosts(c.posts, draftPosts);

    return { ...c, posts: mergedPosts };
  });
}
